import { MentionCardsProvider } from "@/containers/MentionCardsContext";
import { ErrorResponse, SuccessfulResponse } from "@/api/fetcher_v2";
import { PaginatedMentionCards } from "@/types/PaginatedMentionCards";
import { AnimatePresence, motion } from "motion/react";
import MentionCardNew from "@components/MentionCardNew";
import PaginationNew from "@components/PaginationNew";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@components/ui/empty";
import { MessagesSquareIcon } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import ErrorComponent from "@components/ui/error";
import { getTags } from "@/api/endpoints/tags";
import { useQuery } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { Skeleton } from "@components/ui/skeleton";

export default function MentionCards({
  isMentionCardsLoading,
  mentionCardsError,
  mentionCardsData,
  onChangePage,
  orgUuid,
  page,
}: {
  mentionCardsData:
    | ErrorResponse<unknown>
    | SuccessfulResponse<PaginatedMentionCards>
    | undefined;
  onChangePage: (page: number) => void;
  mentionCardsError: Nullable<Error>;
  isMentionCardsLoading: boolean;
  orgUuid: Uuid;
  page: number;
}) {
  const { t } = useLingui();

  // Server state
  const { data: tagsData } = useQuery({
    queryKey: [orgUuid, "tags"],
    queryFn: () => getTags(),
    refetchOnWindowFocus: false,
  });

  const tags = tagsData?.ok ? tagsData.data : [];

  const pageCount = mentionCardsData?.ok ? mentionCardsData.data.page_count : 0;

  return (
    <AnimatePresence mode="popLayout">
      {mentionCardsData ? (
        mentionCardsData.ok ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col gap-5 episodes-container"
            key="mentions"
          >
            {mentionCardsData.data.results.length ? (
              <MentionCardsProvider>
                {mentionCardsData.data.results.map((mentionCard) => {
                  return (
                    <MentionCardNew
                      mentionCard={mentionCard}
                      key={mentionCard.episode.id}
                      orgUuid={orgUuid}
                      tags={tags}
                    />
                  );
                })}

                <PaginationNew
                  page={page}
                  max={pageCount}
                  onChangePage={onChangePage}
                />
              </MentionCardsProvider>
            ) : (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <MessagesSquareIcon />
                  </EmptyMedia>
                  <EmptyTitle data-testid="no-mentions">
                    <Trans>Inga träffar</Trans>
                  </EmptyTitle>
                </EmptyHeader>
              </Empty>
            )}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            key="error"
          >
            <ErrorComponent
              heading={t`Åh nej!`}
              description={t`Något gick snett med att ladda träffarna. Vänligen försök igen senare.`}
            />
          </motion.div>
        )
      ) : isMentionCardsLoading ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="flex flex-col gap-5"
          key="skeletons"
        >
          <Skeleton className="w-full h-64" />
          <Skeleton className="w-full h-64" />
          <Skeleton className="w-full h-64" />
        </motion.div>
      ) : mentionCardsError ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          key="error"
        >
          <ErrorComponent
            heading={t`Åh nej!`}
            description={t`Något gick snett med att ladda träffarna. Vänligen försök igen senare.`}
          />
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
